/**
 * PatientDetail — prontuário completo com abas
 * Abas: 👤 Perfil | 📋 Anamnese | ⚖️ Bioimpedância | 🥗 Plano Alimentar | 🔬 Exames
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  patientsService, fichasService, anamneseService,
  bioimpedanciaService, anthropometryService, dietPlansService, patientExamsService,
  patientAppService,
} from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import TabRecomendacoes        from './TabRecomendacoes';
import TabAcompanhamento       from './TabAcompanhamento';
import DistribuicaoPaciente    from './DistribuicaoPaciente';
import DocumentoPacienteModal  from '../Relatorios/DocumentoPacienteModal';
import { Ico, emo, noEmo } from '../ui/Ico';

// ─── Constantes ───────────────────────────────────────────────────────────────

const BRAND        = '#D4A574';
const BRAND2       = '#b8895c';
const ANALISE_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8888';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Normaliza datas do MySQL ("2024-01-15 10:30:00") e ISO ("2024-01-15T10:30:00Z")
const normalizeDate = (d) => d ? String(d).replace(' ', 'T').split('T')[0] : null;
const fmtDate      = (d) => { const s = normalizeDate(d); return s ? new Date(s + 'T00:00:00').toLocaleDateString('pt-BR') : '—'; };
const fmtDateShort = (d) => { const s = normalizeDate(d); return s ? new Date(s + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'; };
const fmtAge = (dob) => {
  if (!dob) return null;
  const today = new Date(), b = new Date(dob + 'T00:00:00');
  let age = today.getFullYear() - b.getFullYear();
  if (today.getMonth() < b.getMonth() || (today.getMonth() === b.getMonth() && today.getDate() < b.getDate())) age--;
  return age;
};
const fmtWA = (w) => w ? w.replace(/\D/g, '') : null;
// Número para wa.me: completa o 55 só quando veio com DDD + número (10/11 dígitos).
const waDigits = (w) => { const d = fmtWA(w); if (!d) return null; return d.length <= 11 ? '55' + d : d; };
// (21) 98888-7777 a partir de 5521988887777 / 21988887777
const fmtFone = (w) => {
  const d = fmtWA(w); if (!d) return '';
  const n = d.length > 11 && d.startsWith('55') ? d.slice(2) : d;
  if (n.length === 11) return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
  if (n.length === 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`;
  return w;
};
const fmtDataBR = (iso) => { if (!iso) return ''; const [y, m, d] = String(iso).slice(0, 10).split('-'); return d && m && y ? `${d}/${m}/${y}` : ''; };
// O app da paciente ainda não está nas lojas: o convite fica escondido até lá.
const APP_PACIENTE_DISPONIVEL = process.env.REACT_APP_PATIENT_APP === '1';

/** Linha de contato do card da paciente. Dado faltando aparece destacado. */
function ContatoItem({ icon, label, value, faltando, acao }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
      <span style={{ width: 28, height: 28, borderRadius: '8px', background: faltando ? '#f5ece2' : '#f6f0ea',
        color: faltando ? '#5a3e2b' : '#736b64', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Ico e={icon} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#736b64', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
        <div style={{ fontSize: '0.86rem', fontWeight: faltando ? 500 : 600, color: faltando ? '#5a3e2b' : '#2f2a26',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={faltando ? undefined : value}>
          {faltando ? faltando : value}
        </div>
      </div>
      {acao}
    </div>
  );
}

// ─── Estilos compartilhados ───────────────────────────────────────────────────

const CARD = { background: '#ffffff', borderRadius: '12px', padding: '1.25rem 1.5rem', marginBottom: '1rem', boxShadow: '0 2px 8px rgba(44,44,44,0.06)', border: '1px solid rgba(212,165,116,0.1)' };
const SECH = { fontSize: '0.92rem', fontWeight: 600, color: '#5a3e2b', borderBottom: '1px solid #ebe3da', paddingBottom: '0.5rem', marginTop: 0, marginBottom: '1rem' };
const INP  = { width: '100%', padding: '0.6rem 0.9rem', border: '1.5px solid #d9d0c6', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none' };
const TXT  = { ...INP, resize: 'vertical' };
const LBL  = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#4a4440', marginBottom: '0.3rem' };
const G2   = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' };
const BTN  = { padding: '0.6rem 1.25rem', background: `linear-gradient(135deg,${BRAND},#e8c9a8)`, color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' };
const BTN2 = { padding: '0.6rem 1.25rem', background: '#f6f0ea', color: '#5a3e2b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' };

// ─── Pequenos componentes reutilizáveis ───────────────────────────────────────

function InfoField({ label, value, children }) {
  if (!value && !children) return null;
  return (
    <div>
      <p style={{ margin: '0 0 0.15rem', fontSize: '0.68rem', fontWeight: 600, color: '#a39b93', textTransform: 'uppercase', letterSpacing: '.04em' }}>{emo(label)}</p>
      <p style={{ margin: 0, fontSize: '0.9rem', color: '#2f2a26', fontWeight: 500 }}>{children || value}</p>
    </div>
  );
}

function ProfileSection({ title, icon, children }) {
  return (
    <div style={CARD}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '0.92rem', fontWeight: 600, color: '#5a3e2b', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <span>{emo(icon)}</span> {emo(title)}
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px,1fr))', gap: '1rem' }}>
        {children}
      </div>
    </div>
  );
}

function YesNoBadge({ value }) {
  return (
    <span style={{ padding: '0.15rem 0.55rem', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 600, background: value ? '#edf4ee' : '#fbf8f5', color: value ? '#2f5236' : '#736b64' }}>
      {value ? 'Sim' : 'Não'}
    </span>
  );
}

function ReturnBadge({ days }) {
  if (days == null) return <span style={{ color: '#a39b93', fontSize: '0.82rem' }}>Sem consultas</span>;
  const color = days <= 30 ? '#2f5236' : days <= 90 ? '#8a5f1a' : '#a8483f';
  const bg    = days <= 30 ? '#edf4ee' : days <= 90 ? '#f8f1e2' : '#f8ebe9';
  const label = days === 0 ? 'Hoje' : days === 1 ? '1 dia atrás' : `${days} dias atrás`;
  return <span style={{ padding: '0.2rem 0.65rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: 600, background: bg, color }}>{emo(label)}</span>;
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', borderBottom: '2px solid #ebe3da', marginBottom: '1.5rem', overflowX: 'auto', gap: 0, flexShrink: 0 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          padding: '0.75rem 1.1rem',
          border: 'none',
          borderBottom: `2.5px solid ${active === t.id ? BRAND : 'transparent'}`,
          background: 'none',
          cursor: 'pointer',
          fontWeight: active === t.id ? 700 : 500,
          fontSize: '0.875rem',
          color: active === t.id ? BRAND2 : '#736b64',
          whiteSpace: 'nowrap',
          marginBottom: '-2px',
          transition: 'all 0.15s',
        }}>
          {emo(t.label)}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ABA 1 — Perfil
// ─────────────────────────────────────────────────────────────────────────────

function TabPerfil({ patient }) {
  const OBJECTIVE_MAP = {
    emagrecimento: 'Emagrecimento', ganho_massa: 'Ganho de Massa', saude: 'Saúde e Bem-estar',
    performance: 'Performance Esportiva', reeducacao: 'Reeducação Alimentar',
    manutencao: 'Manutenção do Peso', gestante: 'Gestante/Lactante',
  };
  const TIPO_MAP = { presencial: 'Presencial', online: 'Online', ambos: 'Presencial e Online' };
  const PLANO_MAP = { mensal: 'Mensal', trimestral: 'Trimestral', semestral: 'Semestral', avulso: 'Consulta Avulsa' };

  const objetivo = patient.objetivo_principal || patient.objective || null;
  const cidade   = [patient.cidade, patient.estado].filter(Boolean).join(' — ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {(objetivo || patient.tipo_consulta_padrao || patient.plano_contratado || patient.como_conheceu) && (
        <ProfileSection title="Consulta" icon="📅">
          {objetivo && <InfoField label="Objetivo">{emo(OBJECTIVE_MAP[objetivo] || objetivo)}</InfoField>}
          {patient.tipo_consulta_padrao && <InfoField label="Modalidade">{emo(TIPO_MAP[patient.tipo_consulta_padrao] || patient.tipo_consulta_padrao)}</InfoField>}
          {patient.plano_contratado && <InfoField label="Plano">{emo(PLANO_MAP[patient.plano_contratado] || patient.plano_contratado)}</InfoField>}
          {patient.como_conheceu && <InfoField label="Como conheceu" value={patient.como_conheceu} />}
        </ProfileSection>
      )}

      {(patient.has_hypertension != null || patient.has_diabetes != null || patient.other_conditions || patient.activity_level) && (
        <ProfileSection title="Saúde" icon="🏥">
          {patient.has_hypertension != null && (
            <div>
              <p style={{ margin: '0 0 0.15rem', fontSize: '0.68rem', fontWeight: 600, color: '#a39b93', textTransform: 'uppercase' }}>Hipertensão</p>
              <YesNoBadge value={patient.has_hypertension} />
            </div>
          )}
          {patient.has_diabetes != null && (
            <div>
              <p style={{ margin: '0 0 0.15rem', fontSize: '0.68rem', fontWeight: 600, color: '#a39b93', textTransform: 'uppercase' }}>Diabetes</p>
              <YesNoBadge value={patient.has_diabetes} />
            </div>
          )}
          {patient.activity_level && <InfoField label="Nível de Atividade" value={patient.activity_level} />}
          {patient.other_conditions && <InfoField label="Outras condições" value={patient.other_conditions} />}
        </ProfileSection>
      )}

      {(patient.initial_weight || patient.initial_height) && (
        <ProfileSection title="Medidas Iniciais" icon="📐">
          {patient.initial_weight && <InfoField label="Peso inicial" value={`${patient.initial_weight} kg`} />}
          {patient.initial_height && <InfoField label="Altura" value={`${parseFloat(patient.initial_height)} cm`} />}
          {patient.initial_weight && patient.initial_height && (() => {
            // initial_height é canônico em cm (ex: 158); legado em metros é convertido
            const cm = parseFloat(patient.initial_height);
            const h = (cm < 3 ? cm * 100 : cm) / 100;
            const imc = (patient.initial_weight / (h * h)).toFixed(1);
            return <InfoField label="IMC inicial" value={imc} />;
          })()}
        </ProfileSection>
      )}

      {(patient.food_restrictions || patient.allergies || patient.food_preferences || patient.favorite_fruits) && (
        <ProfileSection title="Preferências Alimentares" icon="🥗">
          {patient.food_preferences  && <InfoField label="Preferências"    value={patient.food_preferences} />}
          {patient.favorite_fruits   && <InfoField label="Frutas favoritas" value={patient.favorite_fruits} />}
          {patient.food_restrictions && <InfoField label="Restrições"      value={patient.food_restrictions} />}
          {patient.allergies         && <InfoField label="Alergias"        value={patient.allergies} />}
        </ProfileSection>
      )}

      {patient.notes && (
        <div style={CARD}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.92rem', fontWeight: 600, color: '#5a3e2b' }}><Ico e="📝" sp />Observações</h3>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#4a4440', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{patient.notes}</p>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.75rem', color: '#a39b93', alignItems: 'center' }}>
        <span>Cadastro: {fmtDate(patient.created_at)}</span>
        {patient.birth_date && <span>• Nascimento: {fmtDate(patient.birth_date)}</span>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ABA 2 — Anamnese
// ─────────────────────────────────────────────────────────────────────────────

const ANA_INIT = {
  queixa_principal: '', historico_doencas: '', historico_cirurgias: '',
  medicamentos_uso: '', historico_familiar: '', num_refeicoes: '',
  horarios_refeicoes: '', preferencias_alimentares: '', 'intolerâncias_alergias': '',
  habito_intestinal: '', frequencia_intestinal: '', horas_sono: '',
  qualidade_sono: '', pratica_atividade_fisica: 0, tipo_atividade: '',
  frequencia_atividade: '', duracao_atividade: '', consumo_agua: '',
  consumo_alcool: '', observacoes: '',
};

function TabAnamnese({ patientId }) {
  const { showSuccess, showError } = useToast();
  const [form, setForm]         = useState(ANA_INIT);
  const [hasRecord, setHasRecord] = useState(false);
  const [editing, setEditing]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [fichaSource, setFichaSource] = useState(null); // null | 'online' | 'manual'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [anaRes, fichasRes] = await Promise.all([
        anamneseService.getByPatient(patientId),
        fichasService.getAll(),
      ]);

      const hasAnamnese = anaRes.success && anaRes.data;
      if (hasAnamnese) {
        const d = anaRes.data;
        setForm(Object.fromEntries(Object.keys(ANA_INIT).map(k => [k, d[k] ?? ANA_INIT[k]])));
        setHasRecord(true);
        setEditing(false);
      } else {
        setForm(ANA_INIT);
        setHasRecord(false);
        setEditing(true);
      }

      if (!hasAnamnese) {
        setFichaSource(null);
      } else if (fichasRes.success && fichasRes.data) {
        const importada = fichasRes.data.find(
          f => String(f.patient_id) === String(patientId) && f.status === 'importada'
        );
        setFichaSource(importada ? 'online' : 'manual');
      } else {
        setFichaSource('manual');
      }
    } catch { showError('Erro ao carregar anamnese'); }
    finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, patient_id: patientId };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      const res = await anamneseService.upsert(payload);
      if (res.success) { showSuccess('Anamnese salva!'); load(); }
      else showError(res.error || 'Erro ao salvar anamnese');
    } catch { showError('Erro ao salvar anamnese'); }
    finally { setSaving(false); }
  };

  const fmt = (v) => v || <span style={{ color: '#a39b93' }}>Não informado</span>;

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem', color: '#736b64' }}>Carregando anamnese...</div>;

  const sourceBadge = fichaSource === 'online'
    ? { bg: '#edf4ee', border: '#b9d2bd', color: '#2f5236', text: '📋 Preenchida pelo paciente via formulário online' }
    : fichaSource === 'manual'
    ? { bg: '#edf1f6', border: '#b8c7d8', color: '#4f6a86', text: '✏️ Preenchida manualmente pela nutricionista' }
    : { bg: '#fbf8f5', border: '#b8c7d8', color: '#736b64', text: 'Sem anamnese registrada' };

  const SourceBadge = () => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.9rem', borderRadius: '20px', fontSize: '0.82rem', fontWeight: 600, background: sourceBadge.bg, border: `1px solid ${sourceBadge.border}`, color: sourceBadge.color, marginBottom: '1rem' }}>
      {emo(sourceBadge.text)}
    </div>
  );

  if (hasRecord && !editing) {
    const VIEW_SECTIONS = [
      { title: '🩺 Queixa Principal',    rows: [['Queixa', form.queixa_principal]] },
      { title: '🏥 Histórico Clínico',   rows: [['Doenças', form.historico_doencas], ['Cirurgias', form.historico_cirurgias], ['Medicamentos', form.medicamentos_uso]] },
      { title: '👨‍👩‍👧 Histórico Familiar', rows: [['Família', form.historico_familiar]] },
      { title: '🍽️ Hábitos Alimentares', rows: [['Refeições/dia', form.num_refeicoes], ['Horários', form.horarios_refeicoes], ['Preferências', form.preferencias_alimentares], ['Intolerâncias', form['intolerâncias_alergias']]] },
      { title: '🚽 Hábito Intestinal',   rows: [['Funcionamento', form.habito_intestinal], ['Frequência', form.frequencia_intestinal]] },
      { title: '😴 Sono',               rows: [['Horas/noite', form.horas_sono ? `${form.horas_sono}h` : ''], ['Qualidade', form.qualidade_sono]] },
      { title: '🏃 Atividade Física',    rows: [['Pratica', form.pratica_atividade_fisica ? 'Sim' : 'Não'], ...(form.pratica_atividade_fisica ? [['Tipo', form.tipo_atividade], ['Frequência', form.frequencia_atividade], ['Duração', form.duracao_atividade ? `${form.duracao_atividade} min` : '']] : [])] },
      { title: '💧 Consumo',            rows: [['Água/dia', form.consumo_agua ? `${form.consumo_agua} L` : ''], ['Álcool', form.consumo_alcool]] },
      ...(form.observacoes ? [{ title: '📝 Observações', rows: [['', form.observacoes]] }] : []),
    ];
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <SourceBadge />
          <button onClick={() => setEditing(true)} style={BTN}><Ico e="✏" sp />Editar Anamnese</button>
        </div>
        {VIEW_SECTIONS.map(({ title, rows }) => (
          <div key={title} style={CARD}>
            <h3 style={SECH}>{emo(title)}</h3>
            <div style={G2}>
              {rows.filter(([, v]) => v).map(([label, value]) => (
                <div key={label}>
                  {emo(label && <p style={{ margin: '0 0 0.15rem', fontSize: '0.72rem', fontWeight: 600, color: '#736b64', textTransform: 'uppercase' }}>{emo(label)}</p>)}
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#2f2a26', whiteSpace: 'pre-wrap' }}>{value}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <form onSubmit={handleSave}>
      <div style={{ marginBottom: '1rem' }}><SourceBadge /></div>
      <div style={CARD}>
        <h3 style={SECH}><Ico e="🩺" sp />Queixa Principal</h3>
        <label style={LBL}>Queixa principal / motivo da consulta</label>
        <textarea name="queixa_principal" rows={3} style={TXT} value={form.queixa_principal} onChange={handleChange} placeholder="Descreva o motivo da consulta..." />
      </div>

      <div style={CARD}>
        <h3 style={SECH}><Ico e="🏥" sp />Histórico Clínico</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
          {[
            { name: 'historico_doencas',   label: 'Histórico de doenças',   placeholder: 'Doenças crônicas, condições de saúde...', rows: 3 },
            { name: 'historico_cirurgias', label: 'Histórico de cirurgias', placeholder: 'Cirurgias realizadas, datas...', rows: 2 },
            { name: 'medicamentos_uso',    label: 'Medicamentos em uso',    placeholder: 'Nome, dosagem e frequência...', rows: 3 },
          ].map(f => (
            <div key={f.name}>
              <label style={LBL}>{emo(f.label)}</label>
              <textarea name={f.name} rows={f.rows} style={TXT} value={form[f.name]} onChange={handleChange} placeholder={f.placeholder} />
            </div>
          ))}
        </div>
      </div>

      <div style={CARD}>
        <h3 style={SECH}><Ico e="👨‍👩‍👧" sp />Histórico Familiar</h3>
        <label style={LBL}>Doenças na família</label>
        <textarea name="historico_familiar" rows={3} style={TXT} value={form.historico_familiar} onChange={handleChange} placeholder="Diabetes, hipertensão, câncer, obesidade..." />
      </div>

      <div style={CARD}>
        <h3 style={SECH}><Ico e="🍽" sp />Hábitos Alimentares</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
          <div style={G2}>
            <div>
              <label style={LBL}>Nº de refeições por dia</label>
              <input type="number" name="num_refeicoes" min="1" max="12" style={INP} placeholder="Ex: 5" value={form.num_refeicoes} onChange={handleChange} />
            </div>
          </div>
          {[
            { name: 'horarios_refeicoes', label: 'Horários das refeições', placeholder: 'Café 7h, Almoço 12h...', type: 'input' },
            { name: 'preferencias_alimentares', label: 'Preferências alimentares', placeholder: 'Alimentos preferidos...', rows: 2 },
            { name: 'intolerâncias_alergias', label: 'Intolerâncias e alergias', placeholder: 'Lactose, glúten...', rows: 2 },
          ].map(f => (
            <div key={f.name}>
              <label style={LBL}>{emo(f.label)}</label>
              {f.type === 'input'
                ? <input type="text" name={f.name} style={INP} placeholder={f.placeholder} value={form[f.name]} onChange={handleChange} />
                : <textarea name={f.name} rows={f.rows} style={TXT} value={form[f.name]} onChange={handleChange} placeholder={f.placeholder} />
              }
            </div>
          ))}
        </div>
      </div>

      <div style={CARD}>
        <h3 style={SECH}><Ico e="🚽" sp />Hábito Intestinal</h3>
        <div style={G2}>
          <div>
            <label style={LBL}>Funcionamento intestinal</label>
            <select name="habito_intestinal" style={INP} value={form.habito_intestinal} onChange={handleChange}>
              <option value="">Selecione...</option>
              {[['normal','Normal'],['constipado','Constipado'],['diarreia','Diarreia frequente'],['alternado','Alternado']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={LBL}>Frequência intestinal</label>
            <select name="frequencia_intestinal" style={INP} value={form.frequencia_intestinal} onChange={handleChange}>
              <option value="">Selecione...</option>
              {[['mais_1x_dia','Mais de 1x ao dia'],['1x_dia','1x ao dia'],['2-3x_semana','2–3x por semana'],['1x_semana','1x por semana'],['menos_1x_semana','Menos de 1x por semana']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div style={CARD}>
        <h3 style={SECH}><Ico e="😴" sp />Sono</h3>
        <div style={G2}>
          <div>
            <label style={LBL}>Horas de sono por noite</label>
            <input type="number" name="horas_sono" step="0.5" min="0" max="24" style={INP} placeholder="Ex: 7.5" value={form.horas_sono} onChange={handleChange} />
          </div>
          <div>
            <label style={LBL}>Qualidade do sono</label>
            <select name="qualidade_sono" style={INP} value={form.qualidade_sono} onChange={handleChange}>
              <option value="">Selecione...</option>
              {[['otimo','Ótimo'],['bom','Bom'],['regular','Regular'],['ruim','Ruim'],['pessimo','Péssimo']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div style={CARD}>
        <h3 style={SECH}><Ico e="🏃" sp />Atividade Física</h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', cursor: 'pointer' }}>
          <input type="checkbox" name="pratica_atividade_fisica" checked={!!form.pratica_atividade_fisica} onChange={handleChange} />
          <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#4a4440' }}>Pratica atividade física?</span>
        </label>
        {!!form.pratica_atividade_fisica && (
          <div style={G2}>
            <div>
              <label style={LBL}>Tipo de atividade</label>
              <input type="text" name="tipo_atividade" style={INP} placeholder="Musculação, corrida..." value={form.tipo_atividade} onChange={handleChange} />
            </div>
            <div>
              <label style={LBL}>Frequência semanal</label>
              <select name="frequencia_atividade" style={INP} value={form.frequencia_atividade} onChange={handleChange}>
                <option value="">Selecione...</option>
                {['1x','2x','3x','4x','5x','diario'].map(v => <option key={v} value={v}>{v === 'diario' ? 'Diariamente' : `${v} por semana`}</option>)}
              </select>
            </div>
            <div>
              <label style={LBL}>Duração por sessão (min)</label>
              <input type="number" name="duracao_atividade" min="0" style={INP} placeholder="Ex: 60" value={form.duracao_atividade} onChange={handleChange} />
            </div>
          </div>
        )}
      </div>

      <div style={CARD}>
        <h3 style={SECH}><Ico e="💧" sp />Consumo</h3>
        <div style={G2}>
          <div>
            <label style={LBL}>Consumo de água (litros/dia)</label>
            <input type="number" name="consumo_agua" step="0.1" min="0" style={INP} placeholder="Ex: 2.0" value={form.consumo_agua} onChange={handleChange} />
          </div>
          <div>
            <label style={LBL}>Consumo de álcool</label>
            <select name="consumo_alcool" style={INP} value={form.consumo_alcool} onChange={handleChange}>
              <option value="">Selecione...</option>
              {[['nao_consome','Não consome'],['raramente','Raramente'],['fim_semana','Fins de semana'],['2-3x_semana','2–3x por semana'],['diario','Diariamente']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div style={CARD}>
        <h3 style={SECH}><Ico e="📝" sp />Observações Adicionais</h3>
        <textarea name="observacoes" rows={4} style={TXT} value={form.observacoes} onChange={handleChange} placeholder="Informações adicionais relevantes..." />
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginBottom: '2rem' }}>
        {hasRecord && (
          <button type="button" onClick={() => { setEditing(false); load(); }} style={BTN2}>Cancelar</button>
        )}
        <button type="submit" disabled={saving} style={BTN}>
          {emo(saving ? 'Salvando...' : hasRecord ? '💾 Atualizar Anamnese' : '💾 Salvar Anamnese')}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ABA 3 — Bioimpedância (Histórico + Nova Medição + Gráfico)
// ─────────────────────────────────────────────────────────────────────────────

const BIO_INIT = {
  data_medicao: new Date().toISOString().split('T')[0],
  peso: '', gordura_corporal: '', imc: '', musculos_esqueleticos: '',
  gordura_visceral: '', metabolismo_basal: '', idade_corporal: '', observacoes: '',
};

function TabBioimpedancia({ patientId, patient }) {
  const { showSuccess, showError } = useToast();
  const [view, setView]       = useState('historico'); // 'historico' | 'form'
  const [records, setRecords] = useState([]);
  const [evolution, setEvolution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(BIO_INIT);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [recRes, evRes] = await Promise.all([
        bioimpedanciaService.getByPatient(patientId),
        bioimpedanciaService.getEvolution(patientId, 20),
      ]);
      if (recRes.success) setRecords(recRes.data || []);
      if (evRes.success) {
        const d = evRes.data;
        setEvolution(Array.isArray(d) ? d : (d.records || []));
      }
    } catch { showError('Erro ao carregar bioimpedância'); }
    finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.peso) { showError('Peso é obrigatório'); return; }
    setSaving(true);
    try {
      const payload = { ...form, patient_id: patientId, tipo_consulta: 'presencial' };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      const res = editingId
        ? await bioimpedanciaService.update(editingId, payload)
        : await bioimpedanciaService.create(payload);
      if (res.success) {
        showSuccess(editingId ? 'Medição atualizada!' : 'Medição registrada!');
        setForm(BIO_INIT); setEditingId(null); setView('historico');
        load();
      } else { showError(res.error || 'Erro ao salvar'); }
    } catch { showError('Erro ao salvar medição'); }
    finally { setSaving(false); }
  };

  const handleEdit = (r) => {
    setForm({
      data_medicao:          r.data_medicao || BIO_INIT.data_medicao,
      peso:                  r.peso ?? '',
      gordura_corporal:      r.gordura_corporal ?? '',
      imc:                   r.imc ?? '',
      musculos_esqueleticos: r.musculos_esqueleticos ?? '',
      gordura_visceral:      r.gordura_visceral ?? '',
      metabolismo_basal:     r.metabolismo_basal ?? '',
      idade_corporal:        r.idade_corporal ?? '',
      observacoes:           r.observacoes ?? '',
    });
    setEditingId(r.id);
    setView('form');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta medição?')) return;
    const res = await bioimpedanciaService.delete(id);
    if (res.success) { showSuccess('Medição removida'); load(); }
  };

  const chartData = [...evolution].reverse().map(r => ({
    ...r,
    label: new Date(r.data_medicao + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
  }));

  const TH = { padding: '0.65rem 0.75rem', textAlign: 'left', background: '#fbf8f5', borderBottom: '2px solid #d9d0c6', fontWeight: 600, color: '#4a4440', fontSize: '0.82rem', whiteSpace: 'nowrap' };
  const TD = { padding: '0.6rem 0.75rem', borderBottom: '1px solid #ebe3da', color: '#2f2a26', fontSize: '0.85rem', whiteSpace: 'nowrap' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['historico', 'form'].map(v => (
            <button key={v} onClick={() => { setView(v); if (v === 'form' && !editingId) setForm(BIO_INIT); }}
              style={{ padding: '0.45rem 1rem', border: `1.5px solid ${view === v ? BRAND : '#d9d0c6'}`, borderRadius: '8px', background: view === v ? `${BRAND}18` : '#ffffff', color: view === v ? BRAND2 : '#4a4440', fontWeight: view === v ? 700 : 500, cursor: 'pointer', fontSize: '0.85rem' }}>
              {emo(v === 'historico' ? '📋 Histórico' : editingId ? '✏️ Editar' : '➕ Nova Medição')}
            </button>
          ))}
        </div>
        {view === 'historico' && records.length > 0 && (
          <button onClick={() => { setForm(BIO_INIT); setEditingId(null); setView('form'); }} style={BTN}>+ Nova Medição</button>
        )}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#736b64' }}>Carregando...</div>}

      {!loading && view === 'historico' && (
        <>
          {records.length === 0 ? (
            <div style={{ ...CARD, textAlign: 'center', padding: '2.5rem' }}>
              <p style={{ color: '#736b64', margin: '0 0 1rem' }}>Nenhuma medição registrada ainda.</p>
              <button onClick={() => setView('form')} style={BTN}>+ Registrar primeira medição</button>
            </div>
          ) : (
            <div style={{ ...CARD, overflowX: 'auto', marginBottom: '1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Data','Peso','Gordura%','IMC','Músculos%','G.Visceral','Met.Basal','Idade Corp.',''].map(h => (
                      <th key={h} style={TH}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r.id}>
                      <td style={TD}>{fmtDateShort(r.data_medicao)}</td>
                      <td style={TD}>{r.peso != null ? `${r.peso} kg` : '—'}</td>
                      <td style={TD}>{r.gordura_corporal != null ? `${r.gordura_corporal}%` : '—'}</td>
                      <td style={TD}>{r.imc ?? '—'}</td>
                      <td style={TD}>{r.musculos_esqueleticos != null ? `${r.musculos_esqueleticos}%` : '—'}</td>
                      <td style={TD}>{r.gordura_visceral ?? '—'}</td>
                      <td style={TD}>{r.metabolismo_basal != null ? `${r.metabolismo_basal} kcal` : '—'}</td>
                      <td style={TD}>{r.idade_corporal != null ? `${r.idade_corporal} anos` : '—'}</td>
                      <td style={TD}>
                        <button onClick={() => handleEdit(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', marginRight: '0.25rem' }}><Ico e="✏" /></button>
                        <button onClick={() => handleDelete(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}><Ico e="🗑" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {chartData.length >= 2 && (
            <div style={CARD}>
              <h3 style={SECH}><Ico e="📈" sp />Evolução ao Longo do Tempo</h3>
              {[
                { title: 'Peso (kg)', key: 'peso', color: BRAND },
                { title: 'Gordura Corporal (%)', key: 'gordura_corporal', color: '#e8c9a8' },
                { title: 'Músculos Esqueléticos (%)', key: 'musculos_esqueleticos', color: '#7189a6' },
              ].map(({ title, key, color }) => (
                <div key={key} style={{ marginBottom: '1.5rem' }}>
                  <p style={{ margin: '0 0 0.4rem', fontSize: '0.82rem', fontWeight: 600, color: '#736b64' }}>{emo(title)}</p>
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={chartData} margin={{ top: 4, right: 16, left: -10, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ebe3da" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey={key} stroke={color} strokeWidth={2} dot={{ fill: color, r: 3 }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {!loading && view === 'form' && (
        <div style={CARD}>
          <h3 style={SECH}>{editingId ? 'Editar Medição' : 'Nova Medição Presencial'}</h3>
          <form onSubmit={handleSave}>
            <div style={G2}>
              <div>
                <label style={LBL}>Data da Medição *</label>
                <input type="date" name="data_medicao" required style={INP} value={form.data_medicao} onChange={handleChange} />
              </div>
              <div>
                <label style={LBL}>Peso (kg) *</label>
                <input type="number" name="peso" required step="0.1" min="0" style={INP} placeholder="Ex: 72.5" value={form.peso} onChange={handleChange} />
              </div>
              <div>
                <label style={LBL}>Gordura Corporal (%)</label>
                <input type="number" name="gordura_corporal" step="0.1" min="0" max="100" style={INP} placeholder="Ex: 24.5" value={form.gordura_corporal} onChange={handleChange} />
              </div>
              <div>
                <label style={LBL}>IMC</label>
                <input type="number" name="imc" step="0.1" min="0" style={INP} placeholder="Ex: 23.8" value={form.imc} onChange={handleChange} />
              </div>
              <div>
                <label style={LBL}>Músculos Esqueléticos (%)</label>
                <input type="number" name="musculos_esqueleticos" step="0.1" min="0" max="100" style={INP} placeholder="Ex: 35.2" value={form.musculos_esqueleticos} onChange={handleChange} />
              </div>
              <div>
                <label style={LBL}>Gordura Visceral (nível)</label>
                <input type="number" name="gordura_visceral" step="1" min="1" max="30" style={INP} placeholder="Ex: 8" value={form.gordura_visceral} onChange={handleChange} />
              </div>
              <div>
                <label style={LBL}>Metabolismo Basal (kcal)</label>
                <input type="number" name="metabolismo_basal" step="1" min="0" style={INP} placeholder="Ex: 1540" value={form.metabolismo_basal} onChange={handleChange} />
              </div>
              <div>
                <label style={LBL}>Idade Corporal (anos)</label>
                <input type="number" name="idade_corporal" step="1" min="1" max="120" style={INP} placeholder="Ex: 35" value={form.idade_corporal} onChange={handleChange} />
              </div>
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <label style={LBL}>Observações</label>
              <textarea name="observacoes" rows={2} style={TXT} placeholder="Notas adicionais..." value={form.observacoes} onChange={handleChange} />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <button type="button" onClick={() => { setForm(BIO_INIT); setEditingId(null); setView('historico'); }} style={BTN2}>Cancelar</button>
              <button type="submit" disabled={saving} style={BTN}>{emo(saving ? 'Salvando...' : editingId ? '💾 Atualizar' : '💾 Registrar')}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ABA 3b — Dados Antropométricos (circunferências + dobras cutâneas + derivados)
// ─────────────────────────────────────────────────────────────────────────────

const ANTHRO_CIRC = [
  ['circ_pescoco', 'Pescoço'], ['circ_torax', 'Tórax'], ['circ_cintura', 'Cintura'],
  ['circ_abdomen', 'Abdômen'], ['circ_quadril', 'Quadril'], ['circ_braco_relaxado', 'Braço relaxado'],
  ['circ_braco_contraido', 'Braço contraído'], ['circ_antebraco', 'Antebraço'],
  ['circ_coxa', 'Coxa'], ['circ_panturrilha', 'Panturrilha'],
];
const ANTHRO_DOBRAS = [
  ['dc_peitoral', 'Peitoral'], ['dc_axilar_media', 'Axilar média'], ['dc_triceps', 'Tríceps'],
  ['dc_subescapular', 'Subescapular'], ['dc_abdominal', 'Abdominal'],
  ['dc_suprailiaca', 'Suprailíaca'], ['dc_coxa', 'Coxa'],
];

const ANTHRO_INIT = {
  data_medicao: new Date().toISOString().split('T')[0],
  tipo_consulta: 'presencial', peso: '', altura: '', observacoes: '',
  ...Object.fromEntries(ANTHRO_CIRC.map(([k]) => [k, ''])),
  ...Object.fromEntries(ANTHRO_DOBRAS.map(([k]) => [k, ''])),
};

const numOrNull = (v) => (v === '' || v == null ? null : parseFloat(v));

function idadeDe(birthDate) {
  if (!birthDate) return null;
  const b = new Date(birthDate + 'T00:00:00');
  if (isNaN(b)) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age;
}

/** Espelha computeDerived do backend — preview ao vivo (Jackson-Pollock 7 + Siri). */
function anthroDerivados(form, patient) {
  const peso = numOrNull(form.peso);
  const altura = numOrNull(form.altura);
  const cintura = numOrNull(form.circ_cintura);
  const quadril = numOrNull(form.circ_quadril);
  const out = { imc: null, rcq: null, soma_dobras: null, percentual_gordura: null, massa_gorda: null, massa_magra: null };

  if (peso && altura && altura > 0) {
    const m = altura / 100;
    out.imc = +(peso / (m * m)).toFixed(2);
  }
  if (cintura && quadril && quadril > 0) out.rcq = +(cintura / quadril).toFixed(2);

  const vals = ANTHRO_DOBRAS.map(([k]) => numOrNull(form[k]));
  const presentes = vals.filter((v) => v != null);
  const todas = presentes.length === ANTHRO_DOBRAS.length;
  if (presentes.length) out.soma_dobras = +presentes.reduce((a, b) => a + b, 0).toFixed(1);

  const age = idadeDe(patient?.birth_date);
  const gender = patient?.gender;
  if (todas && age > 0 && (gender === 'M' || gender === 'F')) {
    const s = out.soma_dobras, s2 = s * s;
    const bd = gender === 'M'
      ? 1.112 - 0.00043499 * s + 0.00000055 * s2 - 0.00028826 * age
      : 1.097 - 0.00046971 * s + 0.00000056 * s2 - 0.00012828 * age;
    if (bd > 0) {
      const pct = 495 / bd - 450;
      if (pct > 0 && pct < 75) {
        out.percentual_gordura = +pct.toFixed(1);
        if (peso) {
          const mg = (peso * pct) / 100;
          out.massa_gorda = +mg.toFixed(2);
          out.massa_magra = +(peso - mg).toFixed(2);
        }
      }
    }
  }
  return out;
}

function TabAntropometria({ patientId, patient }) {
  const { showSuccess, showError } = useToast();
  const [view, setView]       = useState('historico');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(ANTHRO_INIT);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await anthropometryService.getByPatient(patientId);
      if (res.success) setRecords(res.data || []);
    } catch { showError('Erro ao carregar dados antropométricos'); }
    finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => { setForm(ANTHRO_INIT); setEditingId(null); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, patient_id: patientId };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      const res = editingId
        ? await anthropometryService.update(editingId, payload)
        : await anthropometryService.create(payload);
      if (res.success) {
        showSuccess(editingId ? 'Registro atualizado!' : 'Registro salvo!');
        resetForm(); setView('historico'); load();
      } else { showError(res.error || 'Erro ao salvar'); }
    } catch { showError('Erro ao salvar registro'); }
    finally { setSaving(false); }
  };

  const handleEdit = (r) => {
    const next = { ...ANTHRO_INIT };
    Object.keys(ANTHRO_INIT).forEach(k => { next[k] = r[k] ?? (k === 'tipo_consulta' ? 'presencial' : ''); });
    next.data_medicao = r.data_medicao || ANTHRO_INIT.data_medicao;
    setForm(next); setEditingId(r.id); setView('form');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este registro?')) return;
    const res = await anthropometryService.delete(id);
    if (res.success) { showSuccess('Registro removido'); load(); }
  };

  const derived = anthroDerivados(form, patient);
  const semSexoData = !(patient?.gender === 'M' || patient?.gender === 'F') || !patient?.birth_date;

  const chartData = [...records].reverse().map(r => ({
    ...r,
    label: fmtDateShort(r.data_medicao),
  }));

  const TH = { padding: '0.65rem 0.75rem', textAlign: 'left', background: '#fbf8f5', borderBottom: '2px solid #d9d0c6', fontWeight: 600, color: '#4a4440', fontSize: '0.82rem', whiteSpace: 'nowrap' };
  const TD = { padding: '0.6rem 0.75rem', borderBottom: '1px solid #ebe3da', color: '#2f2a26', fontSize: '0.85rem', whiteSpace: 'nowrap' };
  const STAT = { flex: '1 1 120px', background: '#fbf8f5', border: '1px solid #ebe3da', borderRadius: '10px', padding: '0.75rem 1rem', textAlign: 'center' };
  const STAT_V = { fontSize: '1.25rem', fontWeight: 700, color: BRAND2 };
  const STAT_L = { fontSize: '0.72rem', color: '#736b64', marginTop: '0.15rem' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['historico', 'form'].map(v => (
            <button key={v} onClick={() => { setView(v); if (v === 'form' && !editingId) setForm(ANTHRO_INIT); }}
              style={{ padding: '0.45rem 1rem', border: `1.5px solid ${view === v ? BRAND : '#d9d0c6'}`, borderRadius: '8px', background: view === v ? `${BRAND}18` : '#ffffff', color: view === v ? BRAND2 : '#4a4440', fontWeight: view === v ? 700 : 500, cursor: 'pointer', fontSize: '0.85rem' }}>
              {emo(v === 'historico' ? '📋 Histórico' : editingId ? '✏️ Editar' : '➕ Nova Avaliação')}
            </button>
          ))}
        </div>
        {view === 'historico' && records.length > 0 && (
          <button onClick={() => { resetForm(); setView('form'); }} style={BTN}>+ Nova Avaliação</button>
        )}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#736b64' }}>Carregando...</div>}

      {!loading && view === 'historico' && (
        <>
          {records.length === 0 ? (
            <div style={{ ...CARD, textAlign: 'center', padding: '2.5rem' }}>
              <p style={{ color: '#736b64', margin: '0 0 1rem' }}>Nenhuma avaliação antropométrica registrada ainda.</p>
              <button onClick={() => setView('form')} style={BTN}>+ Registrar primeira avaliação</button>
            </div>
          ) : (
            <div style={{ ...CARD, overflowX: 'auto', marginBottom: '1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Data','Modalidade','Peso','IMC','% Gordura','RCQ','Σ Dobras','Cintura',''].map(h => (
                      <th key={h} style={TH}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r.id}>
                      <td style={TD}>{fmtDateShort(r.data_medicao)}</td>
                      <td style={TD}>{emo(r.tipo_consulta === 'online' ? '💻 Online' : '🏥 Presencial')}</td>
                      <td style={TD}>{r.peso != null ? `${r.peso} kg` : '—'}</td>
                      <td style={TD}>{r.imc ?? '—'}</td>
                      <td style={TD}>{r.percentual_gordura != null ? `${r.percentual_gordura}%` : '—'}</td>
                      <td style={TD}>{r.rcq ?? '—'}</td>
                      <td style={TD}>{r.soma_dobras != null ? `${r.soma_dobras} mm` : '—'}</td>
                      <td style={TD}>{r.circ_cintura != null ? `${r.circ_cintura} cm` : '—'}</td>
                      <td style={TD}>
                        <button onClick={() => handleEdit(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', marginRight: '0.25rem' }}><Ico e="✏" /></button>
                        <button onClick={() => handleDelete(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}><Ico e="🗑" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {chartData.length >= 2 && (
            <div style={CARD}>
              <h3 style={SECH}><Ico e="📈" sp />Evolução ao Longo do Tempo</h3>
              {[
                { title: 'Peso (kg)', key: 'peso', color: BRAND },
                { title: '% de Gordura Corporal', key: 'percentual_gordura', color: '#e8c9a8' },
                { title: 'Relação Cintura/Quadril (RCQ)', key: 'rcq', color: '#7189a6' },
              ].map(({ title, key, color }) => (
                <div key={key} style={{ marginBottom: '1.5rem' }}>
                  <p style={{ margin: '0 0 0.4rem', fontSize: '0.82rem', fontWeight: 600, color: '#736b64' }}>{emo(title)}</p>
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={chartData} margin={{ top: 4, right: 16, left: -10, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ebe3da" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
                      <Tooltip />
                      <Line type="monotone" dataKey={key} stroke={color} strokeWidth={2} dot={{ fill: color, r: 3 }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {!loading && view === 'form' && (
        <div style={CARD}>
          <h3 style={SECH}>{editingId ? 'Editar Avaliação' : 'Nova Avaliação Antropométrica'}</h3>
          <form onSubmit={handleSave}>
            <div style={G2}>
              <div>
                <label style={LBL}>Data da Avaliação *</label>
                <input type="date" name="data_medicao" required style={INP} value={form.data_medicao} onChange={handleChange} />
              </div>
              <div>
                <label style={LBL}>Modalidade</label>
                <select name="tipo_consulta" style={INP} value={form.tipo_consulta} onChange={handleChange}>
                  <option value="presencial">Presencial (consultório)</option>
                  <option value="online">Online</option>
                </select>
              </div>
              <div>
                <label style={LBL}>Peso (kg)</label>
                <input type="number" name="peso" step="0.1" min="0" style={INP} placeholder="Ex: 72.5" value={form.peso} onChange={handleChange} />
              </div>
              <div>
                <label style={LBL}>Altura (cm)</label>
                <input type="number" name="altura" step="0.1" min="0" style={INP} placeholder="Ex: 175" value={form.altura} onChange={handleChange} />
              </div>
            </div>

            <h4 style={{ ...SECH, marginTop: '1.5rem', fontSize: '0.85rem' }}><Ico e="📏" sp />Circunferências (cm)</h4>
            <div style={G2}>
              {ANTHRO_CIRC.map(([k, label]) => (
                <div key={k}>
                  <label style={LBL}>{emo(label)}</label>
                  <input type="number" name={k} step="0.1" min="0" style={INP} value={form[k]} onChange={handleChange} />
                </div>
              ))}
            </div>

            <h4 style={{ ...SECH, marginTop: '1.5rem', fontSize: '0.85rem' }}><Ico e="🤏" sp />Dobras Cutâneas (mm) — protocolo Pollock 7 dobras</h4>
            <div style={G2}>
              {ANTHRO_DOBRAS.map(([k, label]) => (
                <div key={k}>
                  <label style={LBL}>{emo(label)}</label>
                  <input type="number" name={k} step="0.1" min="0" style={INP} value={form[k]} onChange={handleChange} />
                </div>
              ))}
            </div>

            {/* Preview dos derivados */}
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ ...SECH, fontSize: '0.85rem' }}><Ico e="🧮" sp />Cálculos Automáticos</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div style={STAT}><div style={STAT_V}>{derived.imc ?? '—'}</div><div style={STAT_L}>IMC</div></div>
                <div style={STAT}><div style={STAT_V}>{derived.rcq ?? '—'}</div><div style={STAT_L}>RCQ (cintura/quadril)</div></div>
                <div style={STAT}><div style={STAT_V}>{derived.soma_dobras != null ? `${derived.soma_dobras}` : '—'}</div><div style={STAT_L}>Σ Dobras (mm)</div></div>
                <div style={STAT}><div style={STAT_V}>{derived.percentual_gordura != null ? `${derived.percentual_gordura}%` : '—'}</div><div style={STAT_L}>% Gordura (Pollock 7)</div></div>
                <div style={STAT}><div style={STAT_V}>{derived.massa_gorda != null ? `${derived.massa_gorda}` : '—'}</div><div style={STAT_L}>Massa Gorda (kg)</div></div>
                <div style={STAT}><div style={STAT_V}>{derived.massa_magra != null ? `${derived.massa_magra}` : '—'}</div><div style={STAT_L}>Massa Magra (kg)</div></div>
              </div>
              {semSexoData && (
                <p style={{ fontSize: '0.78rem', color: '#b8895c', margin: '0.75rem 0 0' }}>
                  <Ico e="⚠" sp />% de gordura exige sexo (M/F) e data de nascimento no cadastro do paciente, além das 7 dobras preenchidas.
                </p>
              )}
            </div>

            <div style={{ marginTop: '1.25rem' }}>
              <label style={LBL}>Observações</label>
              <textarea name="observacoes" rows={2} style={TXT} placeholder="Notas adicionais..." value={form.observacoes} onChange={handleChange} />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <button type="button" onClick={() => { resetForm(); setView('historico'); }} style={BTN2}>Cancelar</button>
              <button type="submit" disabled={saving} style={BTN}>{emo(saving ? 'Salvando...' : editingId ? '💾 Atualizar' : '💾 Registrar')}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ABA 4 — Plano Alimentar
// ─────────────────────────────────────────────────────────────────────────────

function TabPlano({ patientId, onNavigate }) {
  const [plans, setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dietPlansService.getByPatient(patientId)
      .then(res => {
        if (res.success) {
          setPlans(res.data || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [patientId]);

  const last = plans[0] || null;

  return (
    <div>
      <div style={{ ...CARD, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <p style={{ margin: '0 0 0.25rem', fontWeight: 600, fontSize: '1rem', color: '#2f2a26' }}><Ico e="🥗" sp />Plano Alimentar</p>
          <p style={{ margin: 0, fontSize: '0.83rem', color: '#736b64' }}>Crie e gerencie os planos alimentares desta paciente</p>
        </div>
        <button onClick={() => onNavigate('plano-alimentar', patientId)} style={BTN}>Ver Planos Alimentares →</button>
      </div>

      <DistribuicaoPaciente patientId={patientId} />

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#736b64' }}>Carregando planos...</div>
      ) : plans.length === 0 ? (
        <div style={{ ...CARD, textAlign: 'center', padding: '2.5rem', border: '1.5px dashed #d9d0c6' }}>
          <p style={{ fontSize: '2rem', margin: '0 0 0.5rem' }}><Ico e="🥗" /></p>
          <p style={{ margin: '0 0 1rem', color: '#736b64', fontSize: '0.9rem' }}>Nenhum plano alimentar criado ainda.</p>
          <button onClick={() => onNavigate('plano-alimentar', patientId)} style={BTN}>+ Criar Primeiro Plano</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {plans.slice(0, 5).map((p, i) => (
            <div key={p.id || i} style={{ ...CARD, display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: 0, cursor: 'pointer' }}
              onClick={() => onNavigate('plano-alimentar', patientId)}>
              <div style={{ width: 40, height: 40, borderRadius: '10px', background: i === 0 ? `linear-gradient(135deg,${BRAND},#e8c9a8)` : '#f6f0ea', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>
                <Ico e="🥗" />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 0.15rem', fontWeight: 600, fontSize: '0.9rem', color: '#2f2a26' }}>
                  {emo(p.plan_name || p.name || p.nome || `Plano #${i + 1}`)}
                  {i === 0 && <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', fontWeight: 600, background: '#edf4ee', color: '#2f5236', padding: '0.1rem 0.45rem', borderRadius: '20px' }}>Último</span>}
                </p>
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#736b64' }}>
                  {fmtDate(p.created_at)}
                  {(p.daily_calories || p.total_calories) ? ` · ${p.daily_calories || p.total_calories} kcal` : ''}
                </p>
              </div>
              <span style={{ color: '#d9d0c6', fontSize: '1.1rem' }}>›</span>
            </div>
          ))}
          {plans.length > 5 && (
            <button onClick={() => onNavigate('plano-alimentar', patientId)} style={{ ...BTN2, textAlign: 'center' }}>
              Ver todos os {plans.length} planos →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ABA 5 — Exames (laboratoriais estruturados + uploads da ficha)
// ─────────────────────────────────────────────────────────────────────────────

const EXAM_REFS = {
  glicemia_jejum:      { min: 70,   max: 99,   unit: 'mg/dL',   label: 'Glicemia em jejum' },
  hemoglobina_glicada: { min: null, max: 5.7,  unit: '%',        label: 'HbA1c' },
  insulina_jejum:      { min: null, max: 15,   unit: 'µUI/mL',  label: 'Insulina em jejum' },
  colesterol_total:    { min: null, max: 200,  unit: 'mg/dL',   label: 'Colesterol total' },
  hdl:                 { min: 50,   max: null, unit: 'mg/dL',   label: 'HDL' },
  ldl:                 { min: null, max: 130,  unit: 'mg/dL',   label: 'LDL' },
  triglicerideos:      { min: null, max: 150,  unit: 'mg/dL',   label: 'Triglicerídeos' },
  tsh:                 { min: 0.4,  max: 4.0,  unit: 'mUI/L',   label: 'TSH' },
  t4_livre:            { min: 0.8,  max: 1.8,  unit: 'ng/dL',   label: 'T4 livre' },
  vitamina_d:          { min: 30,   max: 100,  unit: 'ng/mL',   label: 'Vitamina D' },
  vitamina_b12:        { min: 200,  max: 900,  unit: 'pg/mL',   label: 'Vitamina B12' },
  ferritina:           { min: 12,   max: 150,  unit: 'ng/mL',   label: 'Ferritina' },
  ferro_serico:        { min: 60,   max: 170,  unit: 'µg/dL',   label: 'Ferro sérico' },
  magnesio:            { min: 1.6,  max: 2.6,  unit: 'mg/dL',   label: 'Magnésio' },
  acido_urico:         { min: null, max: 6.0,  unit: 'mg/dL',   label: 'Ácido úrico' },
  pcr:                 { min: null, max: 3.0,  unit: 'mg/L',    label: 'PCR-us' },
  creatinina:          { min: 0.5,  max: 1.1,  unit: 'mg/dL',   label: 'Creatinina' },
  tgo:                 { min: null, max: 40,   unit: 'U/L',     label: 'TGO/AST' },
  tgp:                 { min: null, max: 41,   unit: 'U/L',     label: 'TGP/ALT' },
  hemoglobina:         { min: 12,   max: 16,   unit: 'g/dL',    label: 'Hemoglobina' },
  hematocrito:         { min: 36,   max: 48,   unit: '%',        label: 'Hematócrito' },
};

const EXAM_GROUPS = [
  { id: 'glicemia',    label: '🍬 Glicemia',        keys: ['glicemia_jejum', 'hemoglobina_glicada', 'insulina_jejum'] },
  { id: 'lipidograma', label: '❤️ Lipidograma',     keys: ['colesterol_total', 'hdl', 'ldl', 'triglicerideos'] },
  { id: 'tireoide',    label: '🦋 Tireoide',         keys: ['tsh', 't4_livre'] },
  { id: 'micronut',    label: '💊 Micronutrientes',  keys: ['vitamina_d', 'vitamina_b12', 'ferritina', 'ferro_serico', 'magnesio'] },
  { id: 'outros',      label: '🔬 Outros',           keys: ['acido_urico', 'pcr', 'creatinina', 'tgo', 'tgp'] },
  { id: 'hemograma',   label: '🩸 Hemograma',        keys: ['hemoglobina', 'hematocrito'] },
];

const EXAM_KEYS = EXAM_GROUPS.flatMap(g => g.keys);
const EXAM_INIT = Object.fromEntries([['exam_date', new Date().toISOString().split('T')[0]], ['observacoes', ''], ...EXAM_KEYS.map(k => [k, ''])]);
const ALL_GROUPS_OPEN = Object.fromEntries(EXAM_GROUPS.map(g => [g.id, true]));

function examStatus(key, val) {
  if (val === null || val === undefined || val === '') return null;
  const ref = EXAM_REFS[key];
  if (!ref) return null;
  const v = parseFloat(val);
  const tooLow  = ref.min !== null && v < ref.min;
  const tooHigh = ref.max !== null && v > ref.max;
  return (tooLow || tooHigh) ? 'altered' : 'normal';
}

function ExamValueBadge({ fieldKey, value }) {
  const st  = examStatus(fieldKey, value);
  const ref = EXAM_REFS[fieldKey];
  if (!ref || value === null || value === undefined || value === '') return <span style={{ color: '#d9d0c6', fontSize: '0.8rem' }}>—</span>;
  const altered = st === 'altered';
  return (
    <span style={{
      fontSize: '0.82rem', fontWeight: 600,
      color: altered ? '#a8483f' : '#2f5236',
      background: altered ? '#f8ebe9' : '#edf4ee',
      padding: '0.1rem 0.45rem', borderRadius: '10px',
    }}>
      {value} {ref.unit} {emo(altered ? '⚠️' : '✓')}
    </span>
  );
}

// Mini-gráfico de linha (2.5) — sem dependências, SVG puro
function Sparkline({ serie, color = '#D4A574', width = 190, height = 40 }) {
  const vals = serie.map(p => p.valor);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const n = serie.length;
  const pts = serie.map((p, i) => {
    const x = n === 1 ? width / 2 : (i / (n - 1)) * (width - 8) + 4;
    const y = height - 6 - ((p.valor - min) / span) * (height - 12);
    return [x, y];
  });
  const d = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const [lx, ly] = pts[pts.length - 1];
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {pts.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={i === pts.length - 1 ? 3 : 1.8} fill={color} />)}
      <circle cx={lx} cy={ly} r="4.5" fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

function TrendCard({ t }) {
  const melhora = t.delta == null ? null : (
    // HDL e vitaminas: subir é bom; demais: descer é bom (heurística simples)
    ['hdl', 'vitamina_d', 'vitamina_b12', 'ferritina', 'ferro_serico', 'hemoglobina', 't4_livre'].includes(t.marcador)
      ? t.delta > 0 : t.delta < 0
  );
  const cor = t.fora_faixa ? '#c46a60' : '#46714f';
  const seta = t.delta == null ? '' : (t.delta > 0 ? '▲' : (t.delta < 0 ? '▼' : '='));
  const corDelta = melhora == null ? '#736b64' : (melhora ? '#46714f' : '#c46a60');
  return (
    <div style={{ border: '1px solid #ebe3da', borderRadius: '10px', padding: '0.6rem 0.7rem', background: '#ffffff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.2rem' }}>
        <span style={{ fontSize: '0.76rem', fontWeight: 600, color: '#5a3e2b' }}>{emo(t.label)}</span>
        {t.fora_faixa && <span style={{ fontSize: '0.6rem', fontWeight: 600, color: '#c46a60', background: '#f8ebe9', padding: '1px 5px', borderRadius: '6px' }}>fora da faixa</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '1.15rem', fontWeight: 700, color: cor }}>{t.ultimo}</span>
        <span style={{ fontSize: '0.7rem', color: '#736b64' }}>{t.unidade}</span>
        {t.delta != null && (
          <span style={{ fontSize: '0.72rem', fontWeight: 600, color: corDelta, marginLeft: 'auto' }}>
            {seta} {t.delta > 0 ? '+' : ''}{t.delta}
          </span>
        )}
      </div>
      <Sparkline serie={t.serie} color={t.fora_faixa ? '#e8c9a8' : '#b9d2bd'} />
    </div>
  );
}

function TabExames({ patientId, patient, onNavigate }) {
  const { showSuccess, showError } = useToast();
  const [exams,       setExams]       = useState([]);
  const [form,        setForm]        = useState(EXAM_INIT);
  const [editingId,   setEditingId]   = useState(null);
  const [view,        setView]        = useState('history'); // 'history' | 'picker' | 'form'
  const [uploadsView, setUploadsView] = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [parsing,     setParsing]     = useState(false);
  const [aiFields,    setAiFields]    = useState({});
  const [openGroups,  setOpenGroups]  = useState(ALL_GROUPS_OPEN);
  const pdfInputRef = useRef(null);
  const [ficha,   setFicha]   = useState(null);
  const [analise, setAnalise] = useState(null);
  const [trends,  setTrends]  = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [exRes, fichasRes, trRes] = await Promise.all([
        patientExamsService.getByPatient(patientId),
        fichasService.getAll().catch(() => ({ success: false, data: [] })),
        patientExamsService.trends(patientId).catch(() => ({ success: false, data: [] })),
      ]);
      if (exRes.success) setExams(exRes.data || []);
      if (trRes.success) setTrends((trRes.data || []).filter(t => t.serie.length >= 2));
      if (fichasRes.success) {
        const doPaciente = (fichasRes.data || []).filter(f => String(f.patient_id) === String(patientId));
        // Preferir ficha preenchida; senão qualquer token do paciente (fotos do
        // bot ficam anexadas ao token mesmo com a ficha ainda pendente)
        const match = doPaciente.find(f => f.status !== 'pendente') || doPaciente[0];
        if (match) {
          setFicha(match);
          const aRes = await fichasService.getAnalise(match.token).catch(() => null);
          if (aRes?.success) setAnalise(aRes.data);
        }
      }
    } catch { showError('Erro ao carregar exames'); }
    finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm(EXAM_INIT);
    setEditingId(null);
    setAiFields({});
    setOpenGroups(ALL_GROUPS_OPEN);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (aiFields[name]) setAiFields(prev => { const n = { ...prev }; delete n[name]; return n; });
  };

  const toggleGroup = (id) => setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setParsing(true);
    setView('form');
    try {
      const res = await patientExamsService.parsePdf(file);
      if (!res.success) { showError(res.error || 'Não foi possível ler o PDF'); setView('picker'); return; }
      const parsed = res.data || {};
      const newForm = { ...EXAM_INIT };
      const newAi   = {};
      if (parsed.exam_date) { newForm.exam_date = parsed.exam_date; newAi.exam_date = true; }
      EXAM_KEYS.forEach(k => { if (parsed[k] != null) { newForm[k] = String(parsed[k]); newAi[k] = true; } });
      setForm(newForm);
      setAiFields(newAi);
      const count = Object.keys(newAi).length;
      showSuccess(`${count} campo${count !== 1 ? 's' : ''} preenchido${count !== 1 ? 's' : ''} pela IA. Revise e salve.`);
    } catch (err) {
      showError(err?.response?.data?.error || 'Erro ao processar PDF. Verifique se é um PDF digital.');
      setView('picker');
    } finally {
      setParsing(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.exam_date) { showError('Data do exame é obrigatória'); return; }
    setSaving(true);
    try {
      const payload = { ...form, patient_id: patientId };
      EXAM_KEYS.forEach(k => { if (payload[k] === '') payload[k] = null; });
      const res = editingId
        ? await patientExamsService.update(editingId, payload)
        : await patientExamsService.create(payload);
      if (res.success) {
        showSuccess(editingId ? 'Exame atualizado!' : 'Exame salvo!');
        resetForm();
        setView('history');
        load();
      } else showError(res.error || 'Erro ao salvar');
    } catch { showError('Erro ao salvar exame'); }
    finally { setSaving(false); }
  };

  const handleEdit = (exam) => {
    const f = { exam_date: exam.exam_date || EXAM_INIT.exam_date, observacoes: exam.observacoes || '' };
    EXAM_KEYS.forEach(k => { f[k] = exam[k] != null ? String(exam[k]) : ''; });
    setForm(f);
    setEditingId(exam.id);
    setAiFields({});
    setOpenGroups(ALL_GROUPS_OPEN);
    setView('form');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este exame?')) return;
    const res = await patientExamsService.delete(id);
    if (res.success) { showSuccess('Exame removido'); load(); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: '#736b64' }}>Carregando...</div>;

  const tokenHex    = (ficha?.token || '').replace(/[^a-f0-9]/g, '');
  // Fotos corporais têm aba própria (📸 Fotos); aqui ficam só os exames anexados.
  const uploadFiles = (analise?.exames || []).filter(ex => !/^Foto corporal/i.test(ex.original_name || ''));
  const aiCount     = Object.keys(aiFields).length;

  return (
    <div>
      {/* Sub-nav */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {emo([
          { id: false, label: '📊 Valores Laboratoriais' },
          { id: true,  label: `📎 Uploads da Ficha${uploadFiles.length > 0 ? ` (${uploadFiles.length})` : ''}` },
        ].map(({ id, label }) => (
          <button key={String(id)} onClick={() => setUploadsView(id)}
            style={{ padding: '0.45rem 1rem', border: `1.5px solid ${uploadsView === id ? BRAND : '#d9d0c6'}`, borderRadius: '8px', background: uploadsView === id ? `${BRAND}18` : '#ffffff', color: uploadsView === id ? BRAND2 : '#4a4440', fontWeight: uploadsView === id ? 700 : 500, cursor: 'pointer', fontSize: '0.85rem' }}>
            {emo(label)}
          </button>
        )))}
      </div>

      {/* Input PDF sempre montado (ref não pode ficar em bloco condicional) */}
      <input ref={pdfInputRef} type="file" accept=".pdf,application/pdf" style={{ display: 'none' }} onChange={handlePdfUpload} />

      {/* ── Valores Laboratoriais ── */}
      {!uploadsView && (
        <>
          {/* Toolbar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => { resetForm(); setView('history'); }}
                style={{ padding: '0.4rem 0.9rem', border: `1.5px solid ${view === 'history' ? BRAND : '#d9d0c6'}`, borderRadius: '8px', background: view === 'history' ? `${BRAND}18` : '#ffffff', color: view === 'history' ? BRAND2 : '#4a4440', fontWeight: view === 'history' ? 700 : 500, cursor: 'pointer', fontSize: '0.83rem' }}>
                <Ico e="📋" sp />Histórico
              </button>
              {(view === 'picker' || view === 'form') && (
                <span style={{ padding: '0.4rem 0.9rem', border: `1.5px solid ${BRAND}`, borderRadius: '8px', background: `${BRAND}18`, color: BRAND2, fontWeight: 600, fontSize: '0.83rem' }}>
                  {emo(editingId ? '✏️ Editar coleta' : '➕ Nova Coleta')}
                </span>
              )}
            </div>
            {view === 'history' && (
              <button onClick={() => { resetForm(); setView('picker'); }} style={BTN}>
                + Nova Coleta
              </button>
            )}
          </div>

          {/* ── HISTÓRICO ── */}
          {view === 'history' && trends.length > 0 && (
            <div style={{ ...CARD, marginBottom: '0.9rem' }}>
              <h3 style={{ margin: '0 0 0.15rem', fontSize: '0.92rem', fontWeight: 600, color: '#5a3e2b' }}>
                <Ico e="📈" sp />Tendência de marcadores
              </h3>
              <p style={{ margin: '0 0 0.85rem', fontSize: '0.75rem', color: '#736b64' }}>
                Evolução ao longo das coletas · valor mais recente e variação
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '0.6rem' }}>
                {trends.map(t => <TrendCard key={t.marcador} t={t} />)}
              </div>
            </div>
          )}

          {view === 'history' && (
            exams.length === 0 ? (
              <div style={{ ...CARD, textAlign: 'center', padding: '2.5rem', border: '1.5px dashed #d9d0c6' }}>
                <p style={{ fontSize: '2rem', margin: '0 0 0.5rem' }}><Ico e="🧪" /></p>
                <p style={{ margin: '0 0 1.25rem', color: '#736b64', fontSize: '0.9rem' }}>Nenhuma coleta registrada ainda.</p>
                <button onClick={() => { resetForm(); setView('picker'); }} style={BTN}>+ Registrar exames</button>
              </div>
            ) : (
              exams.slice(0, 3).map(exam => {
                const alteredKeys = EXAM_KEYS.filter(k => exam[k] != null && examStatus(k, exam[k]) === 'altered');
                const normalKeys  = EXAM_KEYS.filter(k => exam[k] != null && examStatus(k, exam[k]) === 'normal');
                return (
                  <div key={exam.id} style={{ ...CARD, marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <h3 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 600, color: '#5a3e2b' }}>
                        <Ico e="🧪" sp />Coleta de {new Date(exam.exam_date + 'T12:00:00').toLocaleDateString('pt-BR')}
                      </h3>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button onClick={() => handleEdit(exam)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', padding: '0.2rem 0.35rem' }}><Ico e="✏" /></button>
                        <button onClick={() => handleDelete(exam.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', padding: '0.2rem 0.35rem' }}><Ico e="🗑" /></button>
                      </div>
                    </div>

                    {/* Valores alterados — destaque */}
                    {alteredKeys.length > 0 && (
                      <div style={{ marginBottom: '0.65rem', background: '#f8ebe9', border: '1px solid #f8ebe9', borderRadius: '8px', padding: '0.5rem 0.75rem' }}>
                        <p style={{ margin: '0 0 0.4rem', fontSize: '0.7rem', fontWeight: 600, color: '#c46a60', textTransform: 'uppercase', letterSpacing: '0.04em' }}><Ico e="⚠" sp />Alterados</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1rem' }}>
                          {alteredKeys.map(k => (
                            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                              <span style={{ fontSize: '0.78rem', color: '#4a4440' }}>{emo(EXAM_REFS[k]?.label)}:</span>
                              <ExamValueBadge fieldKey={k} value={exam[k]} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Valores normais — compacto */}
                    {normalKeys.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem 1rem' }}>
                        {normalKeys.map(k => (
                          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            <span style={{ fontSize: '0.76rem', color: '#736b64' }}>{emo(EXAM_REFS[k]?.label)}:</span>
                            <ExamValueBadge fieldKey={k} value={exam[k]} />
                          </div>
                        ))}
                      </div>
                    )}

                    {exam.observacoes && (
                      <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: '#736b64', fontStyle: 'italic' }}><Ico e="📝" /> {exam.observacoes}</p>
                    )}
                  </div>
                );
              })
            )
          )}

          {/* ── PICKER ── */}
          {view === 'picker' && (
            <div style={CARD}>
              <h3 style={{ margin: '0 0 1.25rem', fontSize: '1rem', fontWeight: 600, color: '#2f2a26', textAlign: 'center' }}>
                Como deseja registrar os exames?
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                {emo([
                  {
                    icon: '📎',
                    title: 'Upload de PDF',
                    desc: 'Envie o laudo do laboratório. A IA lê e preenche os campos automaticamente.',
                    action: () => pdfInputRef.current?.click(),
                    accent: BRAND,
                    bg: '#fbf8f5',
                    borderColor: `${BRAND}50`,
                    loadingIcon: '⏳',
                    loading: parsing,
                    loadingText: 'Lendo PDF...',
                  },
                  {
                    icon: '✏️',
                    title: 'Digitar manualmente',
                    desc: 'Preencha os valores um a um nos campos do formulário.',
                    action: () => { resetForm(); setView('form'); },
                    accent: '#4a4440',
                    bg: '#fbf8f5',
                    borderColor: '#d9d0c6',
                    loading: false,
                  },
                ].map(opt => (
                  <button
                    key={opt.title}
                    type="button"
                    disabled={opt.loading}
                    onClick={opt.action}
                    style={{ background: opt.bg, border: `2px solid ${opt.borderColor}`, borderRadius: '12px', padding: '1.5rem 1.25rem', cursor: opt.loading ? 'wait' : 'pointer', textAlign: 'center', outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s', width: '100%' }}
                    onMouseEnter={e => { if (!opt.loading) { e.currentTarget.style.borderColor = opt.accent; e.currentTarget.style.boxShadow = `0 4px 14px ${opt.accent}25`; } }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = opt.borderColor; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <p style={{ fontSize: '2.2rem', margin: '0 0 0.5rem' }}>{emo(opt.loading ? opt.loadingIcon : opt.icon)}</p>
                    <p style={{ margin: '0 0 0.35rem', fontWeight: 600, fontSize: '0.92rem', color: '#2f2a26' }}>{emo(opt.title)}</p>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#736b64', lineHeight: 1.4 }}>
                      {emo(opt.loading ? opt.loadingText : opt.desc)}
                    </p>
                  </button>
                )))}
              </div>
            </div>
          )}

          {/* ── FORMULÁRIO ── */}
          {view === 'form' && (
            <>
              {/* Spinner enquanto IA processa PDF */}
              {parsing && (
                <div style={{ ...CARD, textAlign: 'center', padding: '2.5rem' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', color: BRAND2 }}>
                    <span style={{ width: 22, height: 22, border: `3px solid ${BRAND}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ fontWeight: 600, fontSize: '1rem' }}>Lendo exame com IA...</span>
                  </div>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.83rem', color: '#736b64' }}>Aguarde enquanto extraímos os valores do PDF</p>
                </div>
              )}

              {!parsing && (
                <form onSubmit={handleSave}>
                  <div style={CARD}>
                    {/* Linha: data + banner IA */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                      <div>
                        <label style={LBL}><Ico e="📅" sp />Data da coleta *</label>
                        <input
                          type="date" name="exam_date" required
                          style={{ ...INP, maxWidth: 210, borderColor: aiFields.exam_date ? BRAND : '#d9d0c6', background: aiFields.exam_date ? '#fbf8f5' : '#ffffff' }}
                          value={form.exam_date} onChange={handleChange}
                        />
                      </div>
                      {aiCount > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', color: BRAND2, background: '#fbf8f5', border: `1px solid ${BRAND}`, borderRadius: '8px', padding: '0.3rem 0.75rem', whiteSpace: 'nowrap' }}>
                          <Ico e="✨" /> {aiCount} campo{aiCount !== 1 ? 's' : ''} preenchido{aiCount !== 1 ? 's' : ''} pela IA — revise antes de salvar
                        </div>
                      )}
                    </div>

                    {/* Grupos colapsáveis */}
                    {EXAM_GROUPS.map((group, gi) => {
                      const isOpen      = openGroups[group.id];
                      const filledCount = group.keys.filter(k => form[k] !== '').length;
                      return (
                        <div key={group.id} style={{ borderTop: gi > 0 ? '1px solid #ebe3da' : 'none', paddingTop: gi > 0 ? '0.9rem' : 0, marginBottom: '0.1rem' }}>
                          <button
                            type="button"
                            onClick={() => toggleGroup(group.id)}
                            style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.3rem 0', marginBottom: isOpen ? '0.75rem' : '0.5rem', outline: 'none' }}
                          >
                            <span style={{ fontWeight: 600, fontSize: '0.87rem', color: '#5a3e2b' }}>{emo(group.label)}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {filledCount > 0 && (
                                <span style={{ fontSize: '0.7rem', background: '#ebe3da', color: '#5a3e2b', borderRadius: '10px', padding: '0.1rem 0.4rem', fontWeight: 600 }}>
                                  {filledCount}/{group.keys.length}
                                </span>
                              )}
                              <span style={{ color: '#d9d0c6', fontSize: '0.72rem' }}>{isOpen ? '▲' : '▼'}</span>
                            </div>
                          </button>

                          {isOpen && (
                            <div style={G2}>
                              {group.keys.map(k => {
                                const ref    = EXAM_REFS[k];
                                const st     = examStatus(k, form[k]);
                                const isAI   = !!aiFields[k];
                                const border = isAI ? BRAND : (st === 'altered' ? '#e3b3ad' : st === 'normal' ? '#b9d2bd' : '#d9d0c6');
                                const ph     = ref.min != null && ref.max != null ? `${ref.min}–${ref.max}` : ref.max != null ? `< ${ref.max}` : `> ${ref.min}`;
                                return (
                                  <div key={k}>
                                    <label style={{ ...LBL, marginBottom: '0.25rem' }}>
                                      {emo(ref.label)}
                                      <span style={{ fontWeight: 400, color: '#a39b93', marginLeft: '0.2rem' }}>({ref.unit})</span>
                                      {isAI && <span style={{ marginLeft: '0.35rem', fontSize: '0.68rem', color: BRAND, fontWeight: 600 }}><Ico e="✨" /></span>}
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                      <input
                                        type="number" step="any" name={k}
                                        style={{ ...INP, flex: 1, minWidth: 0, borderColor: border, background: isAI ? '#fbf8f5' : '#ffffff' }}
                                        placeholder={ph}
                                        value={form[k]} onChange={handleChange}
                                      />
                                      {form[k] !== '' && st && (
                                        <span style={{ flexShrink: 0, fontSize: '0.7rem', fontWeight: 600, color: st === 'altered' ? '#a8483f' : '#2f5236', background: st === 'altered' ? '#f8ebe9' : '#edf4ee', padding: '0.2rem 0.45rem', borderRadius: '8px', whiteSpace: 'nowrap' }}>
                                          {emo(st === 'altered' ? '⚠️ Alt.' : '✓ OK')}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Observações */}
                    <div style={{ borderTop: '1px solid #ebe3da', paddingTop: '0.9rem', marginTop: '0.4rem' }}>
                      <label style={LBL}>Observações</label>
                      <textarea name="observacoes" rows={2} style={TXT} placeholder="Anotações adicionais..." value={form.observacoes} onChange={handleChange} />
                    </div>

                    {/* Botões */}
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
                      <button type="button" onClick={() => { resetForm(); setView('history'); }} style={BTN2}>Cancelar</button>
                      <button type="submit" disabled={saving} style={BTN}>
                        {emo(saving ? 'Salvando...' : editingId ? '💾 Atualizar' : '💾 Salvar Exames')}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </>
          )}
        </>
      )}

      {/* ── Uploads da Ficha Online ── */}
      {uploadsView && (
        <>
          {!ficha ? (
            <div style={{ ...CARD, textAlign: 'center', padding: '2.5rem', border: '1.5px dashed #d9d0c6' }}>
              <p style={{ fontSize: '2rem', margin: '0 0 0.5rem' }}><Ico e="🔬" /></p>
              <p style={{ margin: '0 0 0.25rem', fontWeight: 600, color: '#4a4440' }}>Nenhuma ficha vinculada</p>
              <p style={{ margin: '0 0 1.25rem', fontSize: '0.83rem', color: '#736b64' }}>
                Os exames são enviados pela paciente ao preencher a Ficha Online.
              </p>
              <button onClick={() => onNavigate('fichas-online')} style={BTN}>Ir para Fichas Online →</button>
            </div>
          ) : (
            <>
              <div style={{ ...CARD, display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <span style={{ padding: '0.2rem 0.65rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, background: '#edf1f6', color: '#4f6a86' }}>
                  Ficha: {emo(ficha.status)}
                </span>
                <span style={{ fontSize: '0.82rem', color: '#736b64' }}>Token: {ficha.token.slice(0, 8)}…</span>
                <div style={{ flex: 1 }} />
                <button onClick={() => onNavigate('fichas-online')} style={{ ...BTN2, fontSize: '0.82rem', padding: '0.4rem 0.9rem' }}>
                  <Ico e="📋" sp />Ver Fichas Online
                </button>
              </div>

              {uploadFiles.length === 0 ? (
                <div style={{ ...CARD, textAlign: 'center', padding: '2rem', border: '1.5px dashed #d9d0c6' }}>
                  <p style={{ margin: 0, color: '#736b64', fontSize: '0.9rem' }}><Ico e="📎" sp />Nenhum exame anexado pela paciente</p>
                </div>
              ) : (
                <div style={CARD}>
                  <h3 style={SECH}><Ico e="📎" sp />Exames e fotos anexados ({uploadFiles.length})</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
                    {uploadFiles.map((ex, i) => {
                      const url     = `${ANALISE_BASE}/uploads/exames/${tokenHex}/${ex.filename}`;
                      const isImage = ex.mime_type?.startsWith('image/');
                      return (
                        <div key={i} style={{ background: '#fbf8f5', borderRadius: '10px', border: '1px solid #edf1f6', overflow: 'hidden' }}>
                          {isImage ? (
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              <img src={url} alt={ex.original_name} style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
                            </a>
                          ) : (
                            <div style={{ height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.2rem' }}><Ico e="📄" /></div>
                          )}
                          <div style={{ padding: '0.5rem 0.65rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                            <span style={{ flex: 1, fontSize: '0.75rem', color: '#34495e', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ex.original_name}</span>
                            <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.72rem', background: '#edf1f6', color: '#34495e', borderRadius: '5px', padding: '0.15rem 0.45rem', textDecoration: 'none', fontWeight: 600, flexShrink: 0 }}>↓</a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {analise?.sugestao && (
                <div style={{ ...CARD, background: '#edf1f6', border: '1.5px solid #b8c7d8' }}>
                  <h3 style={{ ...SECH, color: '#7189a6', borderColor: '#edf1f6' }}><Ico e="🤖" sp />Sugestão de Análise por IA</h3>
                  <p style={{ margin: 0, fontSize: '0.88rem', color: '#4f6a86', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{analise.sugestao}</p>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente Principal
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'perfil',            label: '👤 Perfil' },
  { id: 'anamnese',          label: '📋 Anamnese' },
  { id: 'bioimpedancia',     label: '⚖️ Bioimpedância' },
  { id: 'antropometria',     label: '📐 Antropometria' },
  { id: 'plano-alimentar',   label: '🥗 Plano Alimentar' },
  { id: 'recomendacoes',     label: '📋 Recomendações' },
  { id: 'acompanhamento',    label: '📅 Acompanhamento' },
  { id: 'exames',            label: '🔬 Exames' },
  { id: 'fotos',             label: '📸 Fotos' },
];

function PatientDetail({ patientId, onNavigate }) {
  const { showError, showSuccess } = useToast();
  const [patient, setPatient]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [tab, setTab]               = useState('perfil');
  const [sendingFicha, setSendingFicha] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [showDocumento, setShowDocumento] = useState(false);
  const [showEnviarPlano, setShowEnviarPlano] = useState(false);

  useEffect(() => {
    setLoading(true);
    patientsService.getById(patientId)
      .then(res => { if (res.success) setPatient(res.data); })
      .catch(() => showError('Erro ao carregar paciente'))
      .finally(() => setLoading(false));
  }, [patientId]);

  const handleConvidarApp = async () => {
    setSendingInvite(true);
    try {
      const res = await patientAppService.invite(patientId);
      if (!res.success) { showError(res.error || 'Erro ao gerar convite'); return; }
      const { code, enviado, wa_link } = res.data;
      if (enviado) {
        showSuccess(`Convite enviado pelo WhatsApp! Código: ${code}`);
      } else {
        showSuccess(`Código ${code} gerado! Abrindo WhatsApp para enviar...`);
        if (wa_link) setTimeout(() => window.open(wa_link, '_blank'), 800);
      }
    } catch { showError('Erro ao gerar convite'); }
    finally { setSendingInvite(false); }
  };

  const handleEnviarFicha = async () => {
    setSendingFicha(true);
    try {
      const res = await fichasService.createToken({
        patient_id: patientId,
        nome_paciente: patient.name,
        whatsapp: patient.whatsapp || patient.phone || '',
        email: patient.email || '',
      });
      if (!res.success) { showError(res.error || 'Erro ao gerar link'); return; }
      await navigator.clipboard.writeText(res.data.link);
      showSuccess('Link copiado! Abrindo WhatsApp...');
      if (res.data.wa_link) setTimeout(() => window.open(res.data.wa_link, '_blank'), 800);
    } catch { showError('Erro ao gerar ficha'); }
    finally { setSendingFicha(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: '1rem', color: '#736b64' }}>
      <div style={{ width: 48, height: 48, borderRadius: '50%', border: '4px solid #f6f0ea', borderTopColor: BRAND, animation: 'spin 0.9s linear infinite' }} />
      <p>Carregando...</p>
    </div>
  );

  if (!patient) return <div style={{ padding: '2rem', color: '#736b64' }}>Paciente não encontrado.</div>;

  const age      = fmtAge(patient.birth_date);
  const initials = (patient.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const waPhone  = waDigits(patient.whatsapp || patient.phone);
  const waUrl    = waPhone ? `https://wa.me/${waPhone}` : null;
  const copiar   = async (txt, nome) => {
    try { await navigator.clipboard.writeText(txt); showSuccess(`${nome} copiado`); } catch { showError('Não foi possível copiar'); }
  };
  const iconBtn  = { width: 30, height: 30, borderRadius: '8px', border: '1px solid #d9d0c6', background: '#ffffff',
    color: '#4a4440', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, textDecoration: 'none' };
  const cidade   = [patient.cidade, patient.estado].filter(Boolean).join(' — ');

  return (
    <div style={{ maxWidth: '1000px' }}>

      {/* ── Breadcrumb + botões ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <button onClick={() => onNavigate('patients')}
          style={{ background: '#f6f0ea', border: 'none', borderRadius: '8px', padding: '0.5rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#5a3e2b' }}>
          ← Pacientes
        </button>
        <h1 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700, color: '#2f2a26', flex: 1 }}>{patient.name}</h1>
        <button onClick={() => onNavigate('patient-edit', patientId)}
          style={{ background: 'linear-gradient(135deg,#D4A574,#e8c9a8)', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
          <Ico e="✏" sp />Editar
        </button>
      </div>

      {/* ── Card de perfil (sempre visível) ── */}
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '1.25rem 1.5rem', boxShadow: '0 2px 12px rgba(44,44,44,0.08)', border: '1px solid rgba(212,165,116,0.12)', marginBottom: '0', display: 'flex', gap: '1.25rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Avatar */}
        <div style={{ flexShrink: 0 }}>
          {patient.foto_url ? (
            <img src={patient.foto_url} alt={patient.name} style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid rgba(212,165,116,0.3)' }} />
          ) : (
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg,#D4A574,#e8c9a8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', fontWeight: 700, color: '#ffffff', border: '3px solid rgba(212,165,116,0.3)' }}>
              {initials}
            </div>
          )}
        </div>

        {/* Info resumida */}
        <div style={{ flex: 1, minWidth: 180 }}>
          <h2 style={{ margin: '0 0 0.2rem', fontSize: '1.2rem', fontWeight: 700, color: '#2f2a26' }}>{patient.name}</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.5rem', alignItems: 'center' }}>
            {age != null   && <span style={{ fontSize: '0.82rem', color: '#736b64' }}>{age} anos</span>}
            {patient.gender && <span style={{ fontSize: '0.82rem', color: '#736b64' }}>• {patient.gender === 'F' ? 'Feminino' : 'Masculino'}</span>}
            {cidade        && <span style={{ fontSize: '0.82rem', color: '#736b64' }}>• {cidade}</span>}
            {patient.status === 'ativo' && (
              <span style={{ padding: '0.1rem 0.45rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 600, background: '#edf4ee', color: '#2f5236' }}>Ativo</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#736b64' }}>
            <span>Última consulta:</span>
            <ReturnBadge days={patient.days_since_last_appt} />
          </div>

          {/* Dados de contato: conferir com a paciente durante a consulta */}
          <div style={{ marginTop: '1rem', paddingTop: '0.9rem', borderTop: '1px solid #f6f0ea' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#4a4440', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                Dados de contato
              </span>
              <button onClick={() => onNavigate('patient-edit', patientId)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-primary-strong, #9a6a42)' }}>
                <Ico e="✏" sp />Corrigir dados
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0.65rem 1rem' }}>
              <ContatoItem icon="💬" label="WhatsApp"
                value={fmtFone(patient.whatsapp || patient.phone)}
                faltando={!waUrl && 'Sem WhatsApp: o plano não vai em PDF pelo WhatsApp'}
                acao={waUrl && <a href={waUrl} target="_blank" rel="noopener noreferrer" style={iconBtn} title="Abrir conversa no WhatsApp"><Ico e="💬" /></a>} />
              <ContatoItem icon="📧" label="E-mail"
                value={patient.email}
                faltando={!patient.email && 'Sem e-mail: o plano não vai por e-mail'}
                acao={patient.email && <button onClick={() => copiar(patient.email, 'E-mail')} style={iconBtn} title="Copiar e-mail"><Ico e="📋" /></button>} />
              {patient.whatsapp && patient.phone && fmtWA(patient.phone) !== fmtWA(patient.whatsapp) && (
                <ContatoItem icon="📞" label="Telefone" value={fmtFone(patient.phone)} />
              )}
              <ContatoItem icon="🎂" label="Nascimento"
                value={fmtDataBR(patient.birth_date)}
                faltando={!patient.birth_date && 'Não informado'} />
              {patient.cpf && <ContatoItem icon="👤" label="CPF" value={patient.cpf} />}
              {cidade && <ContatoItem icon="📍" label="Cidade" value={cidade} />}
            </div>
          </div>
        </div>

        {/* Ações rápidas */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', minWidth: '170px' }}>
          <button onClick={() => setShowEnviarPlano(true)}
            style={{ padding: '0.55rem 0.9rem', borderRadius: '8px', background: 'var(--color-primary-strong, #9a6a42)', color: '#ffffff', border: 'none', fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' }}>
            <Ico e="📤" sp />Enviar plano
          </button>
          <button onClick={() => onNavigate('agenda', patientId)}
            style={{ padding: '0.5rem 0.9rem', borderRadius: '8px', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
            <Ico e="📅" sp />Agendar consulta
          </button>
          <button onClick={handleEnviarFicha} disabled={sendingFicha} title="Gera o link da anamnese online e abre o WhatsApp para enviar"
            style={{ padding: '0.5rem 0.9rem', borderRadius: '8px', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
            {sendingFicha ? 'Gerando...' : <><Ico e="📋" sp />Enviar ficha de anamnese</>}
          </button>
          <button onClick={() => setShowDocumento(true)}
            style={{ padding: '0.5rem 0.9rem', borderRadius: '8px', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
            <Ico e="📄" sp />Gerar documento (PDF)
          </button>
          {APP_PACIENTE_DISPONIVEL && (
            <button onClick={handleConvidarApp} disabled={sendingInvite}
              style={{ padding: '0.5rem 0.9rem', borderRadius: '8px', background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
              {sendingInvite ? 'Gerando...' : <><Ico e="📱" sp />Convidar p/ App</>}
            </button>
          )}
        </div>
      </div>

      {showDocumento && patient && (
        <DocumentoPacienteModal
          patientId={patientId}
          patientName={patient.name}
          onClose={() => setShowDocumento(false)}
        />
      )}

      {showEnviarPlano && patient && (
        <EnviarPlanoModal
          patient={patient}
          patientId={patientId}
          temWhatsapp={!!waUrl}
          onEditar={() => onNavigate('patient-edit', patientId)}
          onClose={() => setShowEnviarPlano(false)}
        />
      )}

      {/* ── Abas ── */}
      <div style={{ background: '#ffffff', borderRadius: '0 0 12px 12px', border: '1px solid rgba(212,165,116,0.12)', borderTop: 'none', padding: '0 1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 12px rgba(44,44,44,0.08)' }}>
        <TabBar tabs={TABS} active={tab} onChange={setTab} />
      </div>

      {/* ── Conteúdo das abas ── */}
      {tab === 'perfil'          && <TabPerfil patient={patient} />}
      {tab === 'anamnese'        && <TabAnamnese patientId={patientId} />}
      {tab === 'bioimpedancia'   && <TabBioimpedancia patientId={patientId} patient={patient} />}
      {tab === 'antropometria'   && <TabAntropometria patientId={patientId} patient={patient} />}
      {tab === 'plano-alimentar' && <TabPlano patientId={patientId} onNavigate={onNavigate} />}
      {tab === 'recomendacoes'   && <TabRecomendacoes patientId={patientId} />}
      {tab === 'acompanhamento'  && <TabAcompanhamento patientId={patientId} />}
      {tab === 'exames'          && <TabExames patientId={patientId} patient={patient} onNavigate={onNavigate} />}
      {tab === 'fotos'           && <TabFotos patientId={patientId} patient={patient} onNavigate={onNavigate} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Aba: Fotos corporais do paciente (enviadas pelo bot na reserva)
// ─────────────────────────────────────────────────────────────────────────────
const POSES_FOTO = ['Frente', 'Costas', 'Lado direito', 'Lado esquerdo'];

function TabFotos({ patientId, patient, onNavigate }) {
  const { showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [ficha,   setFicha]   = useState(null);
  const [fotos,   setFotos]   = useState([]);
  const [lightbox, setLightbox] = useState(null); // url em tela cheia

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const fichasRes = await fichasService.getAll().catch(() => ({ success: false, data: [] }));
        if (!alive) return;
        let match = null;
        if (fichasRes.success) {
          const doPaciente = (fichasRes.data || []).filter(f => String(f.patient_id) === String(patientId));
          match = doPaciente.find(f => f.status !== 'pendente') || doPaciente[0];
        }
        if (!match) { setFicha(null); setFotos([]); return; }
        setFicha(match);
        const aRes = await fichasService.getAnalise(match.token).catch(() => null);
        if (!alive) return;
        const tokenHex = (match.token || '').replace(/[^a-f0-9]/g, '');
        const corporais = (aRes?.data?.exames || [])
          .filter(ex => /^Foto corporal/i.test(ex.original_name || '') && (ex.mime_type || '').startsWith('image/'))
          .map(ex => {
            const num = parseInt((ex.original_name.match(/(\d+)/) || [])[1] || '0', 10);
            return { ...ex, num, url: `${ANALISE_BASE}/uploads/exames/${tokenHex}/${ex.filename}` };
          })
          .sort((a, b) => a.num - b.num);
        setFotos(corporais);
      } catch { showError('Erro ao carregar fotos'); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: '#736b64' }}>Carregando...</div>;

  if (!ficha || fotos.length === 0) {
    return (
      <div style={{ ...CARD, textAlign: 'center', padding: '3rem 2rem', border: '1.5px dashed #d9d0c6' }}>
        <p style={{ fontSize: '2.4rem', margin: '0 0 0.5rem' }}><Ico e="📸" /></p>
        <p style={{ margin: '0 0 0.35rem', fontWeight: 600, color: '#4a4440' }}>Nenhuma foto corporal ainda</p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#736b64', maxWidth: 380, marginInline: 'auto', lineHeight: 1.6 }}>
          A paciente envia as 4 fotos de corpo inteiro (frente, costas e lados) pelo WhatsApp, logo após confirmar a reserva.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ ...CARD, display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#5a3e2b', fontWeight: 700 }}>
            Fotos corporais {patient?.name ? `— ${patient.name}` : ''}
          </h3>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#736b64' }}>
            Enviadas pela paciente pelo WhatsApp para avaliação
          </p>
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ padding: '0.3rem 0.85rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600, background: `${BRAND}20`, color: BRAND2 }}>
          {fotos.length} de 4 fotos
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.1rem' }}>
        {fotos.map((f, i) => {
          const pose = POSES_FOTO[(f.num || i + 1) - 1] || `Foto ${i + 1}`;
          return (
            <div key={i}
              onClick={() => setLightbox(f.url)}
              style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', cursor: 'zoom-in', background: '#f5ece2', border: '1px solid rgba(212,165,116,0.25)', boxShadow: '0 4px 16px rgba(44,44,44,0.10)', aspectRatio: '3 / 4' }}>
              <img src={f.url} alt={pose}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={e => { e.target.style.opacity = 0.15; }} />
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '1.6rem 0.85rem 0.7rem', background: 'linear-gradient(to top, rgba(44,30,20,0.85), rgba(44,30,20,0))', color: '#ffffff' }}>
                <span style={{ fontSize: '0.9rem', fontWeight: 700, letterSpacing: '0.02em', textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>{pose}</span>
              </div>
              <span style={{ position: 'absolute', top: '0.6rem', left: '0.6rem', width: 26, height: 26, borderRadius: '50%', background: 'rgba(255,255,255,0.92)', color: BRAND2, fontSize: '0.8rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}>
                {f.num || i + 1}
              </span>
            </div>
          );
        })}
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(20,14,10,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', cursor: 'zoom-out' }}>
          <img src={lightbox} alt="Foto corporal"
            style={{ maxWidth: '95%', maxHeight: '95%', objectFit: 'contain', borderRadius: '12px', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }} />
          <button onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            style={{ position: 'absolute', top: '1.2rem', right: '1.4rem', width: 42, height: 42, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.15)', color: '#ffffff', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}>
            <Ico e="✕" />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Modal: Enviar Plano por E-mail ───────────────────────────────────────────
/**
 * Envia o PDF do plano à paciente pelo WhatsApp e/ou e-mail (modelo padrão),
 * pelo mesmo envio da tela Plano Alimentar (enviar-plano.php).
 */
function EnviarPlanoModal({ patient, patientId, temWhatsapp, onEditar, onClose }) {
  const { showSuccess, showError } = useToast();
  const temEmail = !!patient.email;
  const [plans, setPlans]     = useState([]);
  const [planId, setPlanId]   = useState('');
  const [canais, setCanais]   = useState({ whatsapp: temWhatsapp, email: temEmail });
  const [sending, setSending] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    dietPlansService.getByPatient(patientId)
      .then(r => {
        if (!r.success) return;
        const lista = r.data || [];
        setPlans(lista);
        const ativo = lista.find(p => p.status === 'ativo') || lista[0];
        if (ativo) setPlanId(String(ativo.id));
      })
      .catch(() => {})
      .finally(() => setLoadingPlans(false));
  }, [patientId]);

  const escolhidos = Object.keys(canais).filter(k => canais[k]);

  const handleSend = async () => {
    if (!planId || !escolhidos.length) return;
    setSending(true); setResultado(null);
    try {
      const r = await dietPlansService.enviarAoPaciente(parseInt(planId, 10), escolhidos);
      if (r.success) {
        setResultado(r.data?.canais || {});
        if (r.data?.algum_sucesso) showSuccess('Plano enviado!');
        else showError('Nenhum canal conseguiu enviar. Veja os detalhes.');
      } else {
        showError(r.error || r.message || 'Não foi possível enviar o plano');
      }
    } catch (e) {
      showError(e.response?.data?.error || 'Erro ao enviar o plano');
    } finally {
      setSending(false);
    }
  };

  const ovl = { position:'fixed', inset:0, zIndex:9200, background:'rgba(0,0,0,0.5)',
    display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' };
  const box = { background:'#ffffff', borderRadius:'14px', padding:'1.75rem', width:'100%',
    maxWidth:'460px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.35)' };
  const lbl = { fontSize:'0.8rem', fontWeight:600, color:'#4a4440', display:'block', marginBottom:'0.35rem' };

  const Canal = ({ k, icon, titulo, destino, disponivel, aviso }) => (
    <label style={{ display:'flex', alignItems:'flex-start', gap:'0.6rem', padding:'0.7rem 0.8rem', borderRadius:'10px',
      border:`1.5px solid ${canais[k] && disponivel ? 'var(--color-primary, #D4A574)' : '#ebe3da'}`,
      background: disponivel ? '#ffffff' : '#fbf8f5', cursor: disponivel ? 'pointer' : 'not-allowed', marginBottom:'0.5rem' }}>
      <input type="checkbox" checked={!!canais[k] && disponivel} disabled={!disponivel}
        onChange={e => setCanais(c => ({ ...c, [k]: e.target.checked }))}
        style={{ marginTop:'0.2rem', accentColor:'var(--color-primary-strong, #9a6a42)', width:16, height:16 }} />
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:'0.88rem', fontWeight:700, color: disponivel ? '#2f2a26' : '#736b64' }}><Ico e={icon} sp />{titulo}</div>
        <div style={{ fontSize:'0.78rem', color: disponivel ? '#736b64' : '#5a3e2b', wordBreak:'break-word' }}>
          {disponivel ? destino : aviso}
        </div>
        {resultado?.[k] && (
          <div style={{ marginTop:'0.3rem', fontSize:'0.78rem', fontWeight:600, color: resultado[k].ok ? '#46714f' : '#a8483f' }}>
            {resultado[k].ok ? `✓ ${resultado[k].motivo || 'enviado'}` : `✕ ${resultado[k].motivo || 'falhou'}`}
          </div>
        )}
      </div>
    </label>
  );

  return (
    <div style={ovl} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={box}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.1rem' }}>
          <h3 style={{ margin:0, fontSize:'1.1rem', fontWeight:700, color:'#2f2a26' }}><Ico e="📤" sp />Enviar plano à paciente</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'1.2rem', color:'#736b64' }}><Ico e="✕" /></button>
        </div>

        {loadingPlans ? (
          <p style={{ color:'#736b64', fontSize:'0.875rem' }}>Carregando planos...</p>
        ) : plans.length === 0 ? (
          <div style={{ padding:'1rem', background:'#fbf8f5', borderRadius:'8px', fontSize:'0.875rem', color:'#5a3e2b', marginBottom:'1rem' }}>
            Esta paciente ainda não tem plano alimentar salvo.
          </div>
        ) : (
          <div style={{ marginBottom:'1rem' }}>
            <label style={lbl}>Plano a enviar</label>
            <select value={planId} onChange={e => { setPlanId(e.target.value); setResultado(null); }}
              style={{ width:'100%', padding:'0.55rem 0.8rem', border:'1.5px solid #d9d0c6', borderRadius:'8px',
                fontSize:'0.875rem', color:'#2f2a26', background:'#ffffff', outline:'none' }}>
              {plans.map(p => (
                <option key={p.id} value={p.id}>
                  {p.plan_name} — {p.daily_calories} kcal ({noEmo(p.status)})
                </option>
              ))}
            </select>
          </div>
        )}

        <span style={lbl}>Enviar por</span>
        <Canal k="whatsapp" icon="💬" titulo="WhatsApp (PDF do plano)"
          destino={fmtFone(patient.whatsapp || patient.phone)} disponivel={temWhatsapp}
          aviso="Paciente sem WhatsApp cadastrado." />
        <Canal k="email" icon="📧" titulo="E-mail (modelo padrão com o PDF)"
          destino={patient.email} disponivel={temEmail}
          aviso="Paciente sem e-mail cadastrado." />
        {(!temWhatsapp || !temEmail) && (
          <button onClick={onEditar}
            style={{ background:'none', border:'none', padding:0, margin:'0 0 0.75rem', cursor:'pointer', fontSize:'0.8rem', fontWeight:600, color:'var(--color-primary-strong, #9a6a42)' }}>
            <Ico e="✏" sp />Completar cadastro da paciente
          </button>
        )}

        <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'0.5rem' }}>
          <button onClick={onClose}
            style={{ padding:'0.6rem 1.2rem', background:'#fbf8f5', border:'none', borderRadius:'8px',
              cursor:'pointer', fontWeight:600, color:'#4a4440', fontSize:'0.875rem' }}>
            {resultado ? 'Fechar' : 'Cancelar'}
          </button>
          <button onClick={handleSend} disabled={sending || !planId || !escolhidos.length}
            style={{ padding:'0.6rem 1.4rem', background:'var(--color-primary-strong, #9a6a42)', color:'#ffffff', border:'none',
              borderRadius:'8px', cursor: (sending || !planId || !escolhidos.length) ? 'not-allowed' : 'pointer',
              fontWeight:600, fontSize:'0.875rem', opacity: (sending || !planId || !escolhidos.length) ? 0.6 : 1 }}>
            {sending ? 'Enviando...' : <><Ico e="📤" sp />Enviar agora</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PatientDetail;
